import { Component, OnInit } from '@angular/core';
import {
    RouteConfigLoadEnd,
    RouteConfigLoadStart,
    Router,
    Scroll,
} from '@angular/router';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { of, Subscription } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    filter,
    switchMap,
    take,
    tap,
} from 'rxjs/operators';

import { AppInitService } from './app.init';
import { AuthStatus } from './auth/auth.model';
import { AuthService } from './auth/auth.service';
import { LoginModalAuthComponent } from './auth/modal.login.auth.component';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from './common/take-until-destroy.decorator';
import { I18nLoaderService } from './i18n/loader.i18n.service';
import { PasswordProfileComponent } from './profile/password.profile.component';

@Component({
    selector: 'cmps',
    templateUrl: './app.component.html',
    host: {
        '[class.cmps-navbar-pad]':
            '((isAuthenticated && didInit) || isReAuthenticating) && !isAtNonNavRoute',
    },
})
@TakeUntilDestroy()
export class AppComponent implements OnInit {
    isInitRunning: boolean;
    isLazyLoading: boolean;
    isAuthenticated: boolean;
    isReAuthenticating: boolean;
    isPaused: boolean;
    isAtNonNavRoute: boolean;
    initStatus: string;
    didBootstrapInit: boolean;
    didInit: boolean;
    private _sub: Subscription;

    private _refLogin: CmpsDialogRef<LoginModalAuthComponent, boolean>;
    private _refLoginResultSub: Subscription;

    private _refPassword: CmpsDialogRef<PasswordProfileComponent, boolean>;
    private _refPasswordResultSub: Subscription;

    constructor(
        private readonly _router: Router,
        private readonly _translateService: TranslateService,
        private readonly _authService: AuthService,
        private readonly _appInitService: AppInitService,
        private readonly _i18nLoaderService: I18nLoaderService,
        private readonly _dialog: CmpsDialog,
    ) {}

    ngOnInit(): void {
        this._router.events.subscribe((event) => {
            if (event instanceof Scroll) {
                const position = event.position || [0, 0];
                window.scrollTo(position[0], position[1]);
            }

            // TODO revise. lazy loaded modules
            if (event instanceof RouteConfigLoadStart) {
                this.isLazyLoading = true;
            }
            if (event instanceof RouteConfigLoadEnd) {
                this.isLazyLoading = false;
            }

            // TODO injectable non-nav paths
            const nonNavPaths = [
                'login',
                'logout',
                'reset-password',
                'td-qtr-check-in/print-preview',
            ];
            this.isAtNonNavRoute = nonNavPaths.some((value) =>
                this._router.isActive(value, false),
            );
        });

        // TODO revise. lazy loaded modules
        this._i18nLoaderService.events
            .pipe(filter((event) => event.type === 'map'))
            .subscribe(() => {
                // use this method instead of resetLang(), so that onTranslationChange is emitted
                this._i18nLoaderService
                    .getTranslation(this._translateService.currentLang)
                    .pipe(
                        take(1),
                        tap((translations) => {
                            this._translateService.setTranslation(
                                this._translateService.currentLang,
                                translations,
                            );
                        }),
                        catchError(() => of(null)),
                    )
                    .subscribe();
            });

        of(null)
            .pipe(
                switchMap(() => this._appInitService.preRun()),
                // eslint-disable-next-line no-return-assign
                tap(() => (this.didBootstrapInit = true)),
                switchMap(() => this._authService.authStatus),
                distinctUntilChanged(),
            )
            .subscribe((authStatus) => {
                console.log('authStatus', authStatus);

                const wasAuthenticated = this.isAuthenticated;
                this.isAuthenticated = authStatus === AuthStatus.AUTHENTICATED;

                try {
                    if (
                        wasAuthenticated &&
                        !this.isAuthenticated &&
                        this._authService.user.hasSaml2AuthMethod()
                    ) {
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        this._router.navigate(['logout']);
                        return;
                    }
                } catch (err) {}

                this._refLoginResultSub?.unsubscribe();
                this._refLogin?.close();

                this._refPasswordResultSub?.unsubscribe();
                this._refPassword?.close();

                if (
                    authStatus === AuthStatus.AUTHENTICATION_LOST &&
                    !this.isReAuthenticating
                ) {
                    this.isReAuthenticating = true;
                    this._dialog.closeAll();
                    this._refLogin = this._dialog.open(
                        LoginModalAuthComponent,
                        {
                            backdropClass: 'cmps-cdk-bg-color-white',
                            disableClose: true,
                        },
                    );
                    this._refLoginResultSub = this._refLogin.result
                        .pipe(
                            tap((didLogin) => {
                                if (!didLogin) {
                                    this.isReAuthenticating = false;
                                    void this._router.navigate(['logout']);
                                }
                            }),
                            takeUntilDestroy(this),
                        )
                        .subscribe();
                    return;
                }

                if (!this.isAuthenticated) {
                    this._initialNavigation();
                    return;
                }

                this.isInitRunning = true;
                this.initStatus = null;
                this.didInit = false;
                if (this._sub) {
                    this._sub.unsubscribe();
                }
                this._sub = this._appInitService
                    .run()
                    // eslint-disable-next-line no-return-assign, n/handle-callback-err
                    .subscribe(
                        (status) => (this.initStatus = status),
                        // eslint-disable-next-line n/handle-callback-err
                        (error) => {
                            this.isInitRunning = false;
                            this.initStatus = null;
                            this.didInit = true;

                            this._initialNavigation();
                        },
                        () => {
                            this.isInitRunning = false;
                            this.initStatus = null;
                            this.didInit = true;

                            if (this.isReAuthenticating) {
                                this.isReAuthenticating = false;
                                return;
                            }

                            if (
                                this.isAuthenticated &&
                                this._authService.user.securityInfo
                                    .forcePasswordReset
                            ) {
                                this.isPaused = true;
                                this._forcePasswordChange();
                                return;
                            }

                            this._initialNavigation();
                        },
                    );
            });
    }

    // TODO revise. may not be needed when router 'useHash' is false
    // noinspection JSMethodCanBeStatic
    skipToMainContent($event): void {
        $event.stopPropagation();
        $event.preventDefault();
        document.querySelector<HTMLElement>('#cmpsMain').focus();
    }

    private _initialNavigation(): void {
        if (!this._router.navigated) {
            this._router.initialNavigation();
        } else {
            const returnTo = this._router.parseUrl(this._router.url).queryParams
                .returnTo;
            void this._router.navigateByUrl(
                returnTo ??
                    this._authService.user.landingConfig?.path ??
                    '/profile',
            );
        }
    }

    private _forcePasswordChange(): void {
        this._refPassword = this._dialog.open(PasswordProfileComponent, {
            backdropClass: 'cmps-profile-password-backdrop',
            disableClose: true,
            data: { forced: true },
        });
        this._refPasswordResultSub = this._refPassword.result
            .pipe(
                tap((changed) => {
                    if (changed) {
                        this.isPaused = false;
                        this._authService.user.securityInfo.forcePasswordReset =
                            false;
                        this._initialNavigation();
                    } else {
                        this.isPaused = false;
                        void this._router.navigate(['logout']);
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
